import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";

const EMPTY = { name: "", email: "", code: "", commissionRate: "10", status: "active" };

export default function Affiliates() {
  const navigate = useNavigate();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/affiliates", { params: { include_deleted: includeDeleted } });
      setItems(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [includeDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name,
      email: a.email || "",
      code: a.code,
      commissionRate: a.commissionRate,
      status: a.status,
    });
    setError("");
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      name: form.name,
      email: form.email || null,
      code: form.code,
      commissionRate: parseFloat(form.commissionRate),
      status: form.status,
    };
    try {
      if (editing) {
        await api.put(`/affiliates/${editing.id}`, payload);
      } else {
        await api.post("/affiliates", payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleStatus = async (a) => {
    const next = a.status === "active" ? "inactive" : "active";
    await api.put(`/affiliates/${a.id}`, { status: next });
    await load();
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete affiliate "${a.name}" from active use? Referral links and new commissions will stop. Accounting history and payouts will be preserved.`)) return;
    try {
      await api.delete(`/affiliates/${a.id}`);
      await load();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const copyLink = async (a) => {
    try {
      await navigator.clipboard.writeText(a.referralUrl);
    } catch (_) {}
    setCopied(a.id);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div data-testid="affiliates-page">
      <header className="page-head">
        <h1>Affiliates</h1>
        <button className="btn btn-primary" data-testid="new-affiliate-btn" onClick={openCreate}>
          + New Affiliate
        </button>
      </header>
      <p className="muted">Affiliates generate referral commissions. Portal access is optional and can be enabled separately.</p>

      <label><input type="checkbox" checked={includeDeleted} onChange={(event) => setIncludeDeleted(event.target.checked)} /> Show deleted affiliates for audit</label>
      {error && !showForm && <div className="alert">{error}</div>}
      {loading ? (
        <div className="loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty" data-testid="affiliates-empty">
          No affiliates yet. Create your first one.
        </div>
      ) : (
        <div className="table-wrap">
          <table data-testid="affiliates-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Rate %</th>
                <th>Status</th>
                <th>Referral Link</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`affiliate-row-${a.code}`}>
                  <td>
                    <div className="cell-strong">{a.name}</div>
                    {a.email && <div className="muted small">{a.email}</div>}
                  </td>
                  <td><code>{a.code}</code></td>
                  <td>{a.commissionRate}</td>
                  <td>
                    <span className={"badge " + (a.status === "active" ? "badge-green" : "badge-gray")}>
                      {a.status}
                    </span>
                  </td>
                  <td className="link-cell">
                    <span className="ref-url" title={a.referralUrl}>{a.referralUrl}</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      data-testid={`copy-link-${a.code}`}
                      disabled={!a.referralUrl} onClick={() => copyLink(a)}
                    >
                      {copied === a.id ? "Copied!" : "Copy"}
                    </button>
                  </td>
                  <td className="actions">
                    <button className="btn btn-primary btn-sm" data-testid={`manage-${a.code}`} onClick={() => navigate(`/affiliates/${a.id}`)}>
                      Manage commissions
                    </button>
                    <button className="btn btn-ghost btn-sm" data-testid={`edit-${a.code}`} disabled={Boolean(a.deletedAt || a.status === "deleted")} onClick={() => openEdit(a)}>
                      Edit
                    </button>
                    <button className="btn btn-ghost btn-sm" data-testid={`toggle-${a.code}`} disabled={Boolean(a.deletedAt || a.status === "deleted")} onClick={() => toggleStatus(a)}>
                      {a.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button className="btn btn-ghost btn-sm danger" data-testid={`delete-${a.code}`} disabled={Boolean(a.deletedAt || a.status === "deleted")} onClick={() => remove(a)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="affiliate-form">
            <h2>{editing ? "Edit Affiliate" : "New Affiliate"}</h2>
            {error && <div className="alert" data-testid="affiliate-form-error">{error}</div>}
            <form onSubmit={submit}>
              <label>Name *</label>
              <input
                data-testid="form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <label>Email (optional)</label>
              <input
                data-testid="form-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <label>Code * (A-Z a-z 0-9 _ -, max 64)</label>
              <input
                data-testid="form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
              <label>Commission Rate % *</label>
              <input
                data-testid="form-rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.commissionRate}
                onChange={(e) => setForm({ ...form, commissionRate: e.target.value })}
                required
              />
              <label>Status</label>
              <select
                data-testid="form-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">active</option>
                <option value="inactive">inactive</option>
              </select>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="form-submit">
                  {editing ? "Save" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
